import { ref, computed, readonly } from 'vue'
import { consola } from 'consola'
import { 
  saveOrderToUserHistory,
  getUserOrdersFromStorage,
  getActiveOrdersFromStorage,
  clearCompletedOrderFromStorage,
  clearAllOrdersFromStorage,
  clearUserOrdersHistory,
} from '../lib/storageUtils'
import type { Order } from '@entities/order'

const logger = consola.withTag('order-storage')

/**
 * Composable для работы с localStorage заказов
 * Управляет историей заказов и активными заказами
 */
export function useOrderStorage() {
  // Состояние для истории заказов
  const userOrders = ref<Order[]>([])
  
  /**
   * Восстанавливает данные из localStorage при загрузке
   */
  function restoreFromStorage(): void {
    if (!process.client) return
    
    try {
      const storedOrders = getUserOrdersFromStorage()
      userOrders.value = storedOrders
    } catch (error) {
      logger.error('Failed to restore orders from localStorage', error)
    }
  }
  
  /**
   * Сохраняет заказ в историю пользователя
   */
  function saveOrderToHistory(order: Order): void {
    if (!process.client) return
    
    try {
      saveOrderToUserHistory(order)
      
      // Обновляем локальное состояние
      const existingIndex = userOrders.value.findIndex(o => o.id === order.id)
      if (existingIndex === -1) {
        userOrders.value.push(order)
      } else {
        userOrders.value[existingIndex] = order
      }
      
    } catch (error) {
      logger.error('Failed to save order to history', error)
    }
  }
  
  /**
   * Очищает только localStorage для завершенных заказов (БЕЗ очистки store!)
   */
  function clearCompletedOrder(): void {
    if (!process.client) return
    
    try {
      clearCompletedOrderFromStorage()
    } catch (error) {
      logger.error('Failed to clear completed order from storage', error)
    }
  }
  
  /**
   * Получает все заказы пользователя из localStorage
   */
  function getActiveOrders(): Order[] {
    if (!process.client) return []
    
    try {
      return getActiveOrdersFromStorage()
    } catch (error) {
      logger.error('Failed to get active orders from storage', error)
      return []
    }
  }
  
  /**
   * Очищает все данные заказов
   */
  function clearAllOrders(): void {
    if (!process.client) return
    
    try {
      clearAllOrdersFromStorage()
      userOrders.value = []
    } catch (error) {
      logger.error('Failed to clear all orders from storage', error)
    }
  }
  
  /**
   * Очищает только историю заказов
   */
  function clearUserHistory(): void {
    if (!process.client) return
    
    try {
      clearUserOrdersHistory()
      userOrders.value = []
    } catch (error) {
      logger.error('Failed to clear user order history', error)
    }
  }
  
  // Computed свойства
  const ordersCount = computed(() => userOrders.value.length)
  const hasOrders = computed(() => userOrders.value.length > 0)
  
  return {
    // State
    userOrders: readonly(userOrders),
    ordersCount,
    hasOrders,
    
    // Actions
    restoreFromStorage,
    saveOrderToHistory,
    clearCompletedOrder,
    getActiveOrders,
    clearAllOrders,
    clearUserHistory,
  }
}
